import { useState, useEffect } from "react";
import { AltsAllocation } from "@/types/investment";
import { StrategyType } from "../../investment-strategy/InvestmentStrategyContext";
import { getDefaultAllocations } from "../defaultAllocations";

export const useAltAllocations = (selectedStrategy?: StrategyType) => {
  const [altAllocations, setAltAllocations] = useState<AltsAllocation | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!selectedStrategy) {
      console.log('No strategy selected, skipping alt allocations fetch');
      setIsLoading(false);
      return;
    }

    try {
      console.log('Loading allocations for strategy:', selectedStrategy);
      
      // Check localStorage first
      const savedAllocations = localStorage.getItem('altAllocations');
      if (savedAllocations) {
        console.log('Found saved allocations in localStorage:', savedAllocations);
        const parsedAllocations = JSON.parse(savedAllocations);
        console.log('Parsed localStorage allocations:', parsedAllocations);
        
        // Only use saved allocations if they match the current strategy
        if (parsedAllocations.strategy_type === selectedStrategy) {
          console.log('Using saved allocations as they match current strategy');
          const { 
            private_equity_allocation,
            private_credit_allocation,
            private_debt_allocation,
            real_estate_allocation,
            hedge_funds_allocation,
            commodities_allocation,
            cryptocurrencies_allocation,
            collectibles_allocation
          } = parsedAllocations;
          
          setAltAllocations({
            private_equity_allocation,
            private_credit_allocation,
            private_debt_allocation,
            real_estate_allocation,
            hedge_funds_allocation,
            commodities_allocation,
            cryptocurrencies_allocation,
            collectibles_allocation
          });
          setIsLoading(false);
          return;
        } else {
          console.log('Saved allocations strategy mismatch. Saved:', parsedAllocations.strategy_type, 'Current:', selectedStrategy);
        }
      } else {
        console.log('No saved allocations found in localStorage');
      }

      // Use default allocations
      const defaultAllocations = getDefaultAllocations(selectedStrategy);
      console.log('Using default allocations:', defaultAllocations);
      setAltAllocations(defaultAllocations);
      
      // Save to localStorage with strategy type
      const allocationsToSave = {
        ...defaultAllocations,
        strategy_type: selectedStrategy
      };
      localStorage.setItem('altAllocations', JSON.stringify(allocationsToSave));
      
    } catch (error) {
      console.error('Error in useAltAllocations:', error);
    } finally {
      setIsLoading(false);
    }
  }, [selectedStrategy]);

  const handleAllocationsChange = (newAllocations: Partial<AltsAllocation>) => {
    if (altAllocations) {
      console.log('Updating allocations with:', newAllocations);
      const updatedAllocations = {
        ...altAllocations,
        ...newAllocations
      };
      console.log('New complete allocation state:', updatedAllocations);
      setAltAllocations(updatedAllocations);
      
      // Save to localStorage with strategy type
      const allocationsToSave = {
        ...updatedAllocations,
        strategy_type: selectedStrategy
      };
      localStorage.setItem('altAllocations', JSON.stringify(allocationsToSave));
    }
  };

  return { altAllocations, isLoading, handleAllocationsChange };
};